import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/react';

import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import { AppRoutes } from '@/utils/routes/router';

/**
 * TODO(Auth0): keep this guard; swap useAuth().isAuthenticated for Auth0 isAuthenticated if needed.
 */
function CognitoProtectedRoute<P extends object>({
  Component,
  props,
}: {
  Component: React.ComponentType<P>;
  props: P;
}) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }
    navigate(AppRoutes.home);
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <div className="checkingAuth"></div>;
  }

  if (!isAuthenticated) {
    return <div className="checkingAuth"></div>;
  }

  return <Component {...props} />;
}

function ClerkProtectedRoute<P extends object>({
  Component,
  props,
}: {
  Component: React.ComponentType<P>;
  props: P;
}) {
  const navigate = useNavigate();
  const { isLoading } = useAuth();
  const clerkAuth = useClerkAuth();

  const authed = clerkAuth.isLoaded && clerkAuth.isSignedIn;
  const loading = !clerkAuth.isLoaded || (clerkAuth.isSignedIn && isLoading);

  useEffect(() => {
    if (loading || authed) {
      return;
    }
    navigate(AppRoutes.signIn);
  }, [authed, loading, navigate]);

  if (loading) {
    return <div className="checkingAuth"></div>;
  }

  if (!authed) {
    return <div className="checkingAuth"></div>;
  }

  return <Component {...props} />;
}

const protectedRoute =
  <P extends object>(Component: React.ComponentType<P>) =>
  (props: P) => {
    if (isClerkAuthEnabled()) {
      return <ClerkProtectedRoute Component={Component} props={props} />;
    }

    return <CognitoProtectedRoute Component={Component} props={props} />;
  };

export default protectedRoute;
